import { Context, FC, PropsWithChildren, useEffect, useState } from 'react';
import { StoreApi } from 'zustand';
import { useAppCache } from '../../store';

/**
 * A higher-order function that creates a custom provider for a store using a given context and store factory.
 *
 * - **`appCacheKey`**: Optional cache key to leverage an app-wide caching mechanism for the store. If provided, the store
 * can be reused from the cache or instantiated and cached on demand.
 * - **`rerender`**: Optional flag to trigger reapplication of the `initialState` to the store on re-renders.
 * - **`children`**: React children elements to render within the context provider.
 * - **`initialState`**: Optional partial initial state to set when the store is instantiated or reused from cache.
 *
 * - **Behavior**:
 *   - If `appCacheKey` is not provided, the store is created fresh on initial render using the `factory` function.
 *   - If `appCacheKey` is provided, the store checks for an existing cache entry.
 *       - If found, it reuses the cached store and optionally applies the `initialState`.
 *       - If not found, it creates the store via the `factory` function, optionally applies `initialState`, and caches it.
 *   - If `rerender` is set to `true`, the `initialState` is reapplied to the store on subsequent renders.
 *
 * - **Return Value**:
 *   - A React functional component that wraps the children inside a context provider, making the store available
 *     throughout the component tree.
 */
export const createStoreProvider = <T, S extends StoreApi<T> = StoreApi<T>, P extends Partial<T> = Partial<T>>(
  ContextProvider: Context<S>,
  factory: () => S
) => {
  const ThemeBoardEntriesStoreProvider: FC<
    PropsWithChildren & { appCacheKey?: string; rerender?: boolean } & P
  > = ({ children, appCacheKey, rerender, ...initialState }) => {
    const [store, setStore] = useState<S>();
    const getAppCacheValue = useAppCache(state => state.get);
    const setAppCacheValue = useAppCache(state => state.set);

    useEffect(() => {
      if (!appCacheKey) {
        const factoryValue = factory();
        if (initialState) {
          factoryValue.setState(initialState as P);
        }
        setStore(factoryValue);
        return;
      }

      const existingCache = getAppCacheValue(appCacheKey);
      if (existingCache) {
        if (initialState) {
          (existingCache as S).setState(initialState as P);
        }
        setStore(existingCache as S);
      } else {
        const factoryValue = factory();
        if (initialState) {
          factoryValue.setState(initialState as P);
        }
        setAppCacheValue(appCacheKey, factoryValue);
        setStore(factoryValue);
      }
    }, [appCacheKey]);

    useEffect(() => {
      if (!rerender) {
        return;
      }
      store?.setState(initialState as P);
    }, [rerender, initialState]);

    if (!store) {
      return null;
    }
    return <ContextProvider.Provider value={store}>{children}</ContextProvider.Provider>;
  };
  return ThemeBoardEntriesStoreProvider;
};
